// REQ-025 AC2 / AC3 — WHICH metrics the spend chart may be coloured by, which way is good, and what the
// reader's two ends default to. The list is nine, and nine is not a chosen ceiling: it is every metric the
// REQ §7 glossary defines that the daily series carries PER DAY, minus the four REQ-025 AC2 rules out
// (Average times seen and People reached need per-day reach, which the series nulls; Share of the daily
// budget used needs the day's budget in force, which `TimeSeries` does not carry; Conversion rate appears
// nowhere else in the product).
//
// **The direction table is NOT written here.** It is `goodDirection` in `data/tables/metrics.json`, which the
// C# Engine reads as the same embedded resource (ARCH §2.4), and `catalogue.test.ts` asserts REQ-025 AC2's
// nine rows against it verbatim — so the table is the one source and the requirement is the one assertion. A
// second copy here is exactly the drift ARCH §2.4 exists to prevent.
//
// **The LABELS are not written here either**, for the same reason and one more: REQ-025 AC2 forbids "CPA",
// "CTR", "CPM" and "impressions" (all four fail the glossary scan), and the registry's own `label` column is
// where the product's words already live — run through `withResultNoun` so one result type reads "Cost per
// purchase" and "Purchases" (REQ-043 AC2). The WORDS of the six swatches are app copy (D-540): this module
// exports their TOKEN names in the designer's order and nothing a user reads.

import { metricDefinition, metricLimitContext, resultTypeDefinition } from '../metrics/index';
import { withResultNoun } from '../messages/resultNouns';
import type { ResultTypeKey } from '../types/entities';

/**
 * The nine metrics of REQ-025 AC2, by their `MetricKey` in the registry. They are a SUBSET of
 * `MetricRegistryKey`, so every one of them has a row in `metrics.json` with a label, a unit and a
 * direction; "Static" is the absence of a metric (`ColorBySettings.metric === null`) and not a member.
 */
export type ColorByMetric =
  | 'costPerResult'
  | 'costPerClick'
  | 'cpm'
  | 'results'
  | 'roas'
  | 'clickRatePct'
  | 'linkClicks'
  | 'spend'
  | 'impressions';

/**
 * REQ-025 AC2's own order — the order the picker lists them in, grouped Cost · Outcome · Delivery · How much.
 * Static is first in the control and is not in this list.
 */
export const COLOR_BY_METRICS: readonly ColorByMetric[] = [
  'costPerResult',
  'costPerClick',
  'cpm',
  'results',
  'roas',
  'clickRatePct',
  'linkClicks',
  'spend',
  'impressions',
];

/** REQ-025 AC2's four groups, so the picker's separators are not a per-app choice. */
export type ColorByGroup = 'cost' | 'outcome' | 'delivery' | 'how_much';

const GROUPS: Readonly<Record<ColorByMetric, ColorByGroup>> = {
  costPerResult: 'cost',
  costPerClick: 'cost',
  cpm: 'cost',
  results: 'outcome',
  roas: 'outcome',
  clickRatePct: 'delivery',
  linkClicks: 'delivery',
  spend: 'how_much',
  impressions: 'how_much',
};

export function colorByGroup(metric: ColorByMetric): ColorByGroup {
  return GROUPS[metric];
}

const METRIC_SET: ReadonlySet<string> = new Set<string>(COLOR_BY_METRICS);

export function isColorByMetric(value: unknown): value is ColorByMetric {
  return typeof value === 'string' && METRIC_SET.has(value);
}

/**
 * REQ-025 AC2's direction column, read off the registry: `down` is lower-is-better (a cost), `up` is
 * higher-is-better (an outcome), `neutral` is the third class REQ-025 AC3 insists on — Spent and Times shown
 * have no good end at all, because colouring a low spend green would tell an owner that spending less is good.
 */
export type ColorByDirection = 'lower_is_better' | 'higher_is_better' | 'neither';

export function colorByDirection(metric: ColorByMetric): ColorByDirection {
  switch (metricDefinition(metric).goodDirection) {
    case 'down':
      return 'lower_is_better';
    case 'up':
      return 'higher_is_better';
    case 'neutral':
      return 'neither';
  }
}

/**
 * The metric's label with REQ-043 AC2's substitution applied — "Cost per result" → "Cost per purchase",
 * "Results" → "Purchases" — so the picker, the gradient key and the tooltip row all read the same words on
 * both builds. `undefined` / `other` / `mixed` keep the word "result".
 */
export function colorByLabel(metric: ColorByMetric, resultType?: ResultTypeKey): string {
  return withResultNoun(metricDefinition(metric).label, resultType);
}

/**
 * The `FormatContext` spelling the metric's own figures take (REQ-261 AC2 / ARCH §8.5), through the ONE
 * derivation `metricLimitContext` — a measured figure, never a limit the owner typed, because every figure
 * this feature shows (a day's value, the two thresholds, the average, the window's low and high) is a
 * measurement of the account. The two threshold FIELDS are parsed by REQ-261 AC2's own parse module, which
 * takes the same context.
 */
export function colorByFormatContext(metric: ColorByMetric): string {
  return metricLimitContext(metric, 'value');
}

// ---- the default ends, as TOKEN NAMES ------------------------------------------------------------------------

/** DS §4.11: a directional pair is a THREE-stop ramp through the neutral middle; a sequential ramp is two. */
export type ColorByRampKind = 'diverging' | 'sequential';

export interface ColorByEnds {
  kind: ColorByRampKind;
  /** The token at the LOW value — the numerically lower end of the scale, which is not always the good one. */
  low: string;
  /** The neutral stop at the thresholds' midpoint; `null` on a two-stop sequential ramp. */
  mid: string | null;
  high: string;
}

/** DS §4.1 / REQ-025 AC3: the good end (teal) and the bad end (vermilion), named, never a literal green / red. */
export const VIZ_GOOD_TOKEN = '--viz-div-pos-3';
export const VIZ_BAD_TOKEN = '--viz-div-neg-3';
export const VIZ_MID_TOKEN = '--viz-div-mid';
export const VIZ_SEQ_LOW_TOKEN = '--viz-seq-2';
export const VIZ_SEQ_HIGH_TOKEN = '--viz-seq-7';
/** The reference ink the average line is drawn in (DS §4.1, minted by T-441), over §4.1's surface halo. */
export const VIZ_AVERAGE_TOKEN = '--viz-average';

/**
 * REQ-025 AC3's six swatches, in the designer's order (T-441). The WORDS are app copy; these are the tokens.
 * Gold (`--viz-cat-4`) is excluded at 2.5:1 and `--viz-seq-1` is never an end — a 24 px column needs ≥ 3:1.
 */
export const COLOR_BY_SWATCH_TOKENS: readonly string[] = [
  VIZ_GOOD_TOKEN,
  VIZ_BAD_TOKEN,
  '--viz-cat-6',
  '--viz-cat-5',
  '--viz-cat-3',
  '--viz-cat-2',
];

/**
 * The smart defaults of REQ-025 AC3, by the metric's direction. On a lower-is-better metric the LOW value is
 * teal and the high vermilion — Jason's "green for low CPA, red for high CPA" in the palette that survives
 * colour-blindness — and on a higher-is-better metric the pair is reversed, which is the whole reason the
 * direction table exists. A metric with no direction gets no good/bad pair at all.
 */
export function colorByDefaultEnds(metric: ColorByMetric): ColorByEnds {
  switch (colorByDirection(metric)) {
    case 'lower_is_better':
      return { kind: 'diverging', low: VIZ_GOOD_TOKEN, mid: VIZ_MID_TOKEN, high: VIZ_BAD_TOKEN };
    case 'higher_is_better':
      return { kind: 'diverging', low: VIZ_BAD_TOKEN, mid: VIZ_MID_TOKEN, high: VIZ_GOOD_TOKEN };
    case 'neither':
      return { kind: 'sequential', low: VIZ_SEQ_LOW_TOKEN, mid: null, high: VIZ_SEQ_HIGH_TOKEN };
  }
}

/** Which ramp a metric mixes in — three stops through the middle, or the two-stop sequential one. */
export function colorByRampKind(metric: ColorByMetric): ColorByRampKind {
  return colorByDirection(metric) === 'neither' ? 'sequential' : 'diverging';
}

// ---- which metrics a SUBJECT can honestly offer --------------------------------------------------------------

/** The three metrics that read the subject's own result type, so the subject's type decides whether they exist. */
const RESULT_BASED: ReadonlySet<ColorByMetric> = new Set<ColorByMetric>([
  'results',
  'costPerResult',
  'roas',
]);

/**
 * Whether this metric can be offered for a subject whose primary result is `resultType` — DS §12 no. 44's
 * `chart.colors.metric.unavailable` state, decided once rather than per build.
 *
 * Two honest refusals:
 *   · a **per-1,000** result (people reached) has no per-day count at all — the series carries reach per
 *     stored window and nulls `MetricSums.reach` on every point, so Purchases / Cost per purchase / Return on
 *     ad spend over a reach ad set would be a figure we do not hold (REQ-025 AC2's own reason for ruling
 *     People reached out);
 *   · **Return on ad spend** needs `action_values`, which only a result type with `hasValue` reports
 *     (`result-types.json`) — on a sign-up account every day's value is `null`, so offering the metric and
 *     then drawing twenty-eight grey columns is a promise the data cannot keep.
 *
 * Everything else is available on every subject: a day may still have no value (`colorByDayValue`), which is
 * a fact about that day and not about the metric.
 */
export function colorByMetricAvailable(
  metric: ColorByMetric,
  resultType: ResultTypeKey | undefined,
): boolean {
  if (!RESULT_BASED.has(metric)) return true;
  if (resultType === undefined) return false;
  const facts = resultTypeDefinition(resultType);
  if (facts.per1000) return false;
  return metric === 'roas' ? facts.hasValue : true;
}

/** The metrics a subject may be coloured by, in REQ-025 AC2's order — `COLOR_BY_METRICS` minus the refusals. */
export function colorByMetricsFor(resultType: ResultTypeKey | undefined): readonly ColorByMetric[] {
  return COLOR_BY_METRICS.filter((metric) => colorByMetricAvailable(metric, resultType));
}
